import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import httpClient from 'utility/httpClient.util'
import { Box } from './Box'
import {
  getReportResourceCode,
  getReportResourceSize,
  isReportResourceOutput,
  resolveReportResourceData,
} from './reportResource.util'

const getResourceRequestParams = (resourceCode, reportParams = {}) => {
  const params = new URLSearchParams()
  params.set('convertQuery', 'true')
  params.set('include', 'reportTemplate')
  params.set('reportCode', resourceCode)

  Object.entries(reportParams || {}).forEach(([key, value]) => {
    if (
      ['reportCode', 'reportTemplateId', 'revisionId'].includes(key) ||
      value === null ||
      typeof value === 'undefined' ||
      typeof value === 'object'
    ) {
      return
    }
    params.set(key, value)
  })

  return params.toString()
}

const getFontClassName = (fontFamily) => {
  const fontClasses = {
    angsana: 'font-angsana',
    THSarabunNew: 'font-sarabun-new',
    Prompt: 'font-prompt',
    sarabun: 'font-sarabun',
  }

  return fontClasses[fontFamily] || 'font-sarabun'
}

const statusStyle = {
  alignItems: 'center',
  borderColor: '#dc3545',
  borderStyle: 'dashed',
  borderWidth: 1,
  color: '#dc3545',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  justifyContent: 'center',
  minHeight: 60,
  textAlign: 'center',
  width: '100%',
}

const EMPTY_REPORT_PARAMS = {}
const NOOP = () => {}
const resourceRequestMap = new Map()

const fetchReportResource = (query) => {
  if (resourceRequestMap.has(query)) {
    return resourceRequestMap.get(query)
  }

  const request = httpClient
    .get('/report/report-webview/find?' + query)
    .then(({ data }) => data.data)
    .finally(() => resourceRequestMap.delete(query))

  resourceRequestMap.set(query, request)
  return request
}

const ReportResourceContent = ({
  item,
  reportParams = EMPTY_REPORT_PARAMS,
  pageNo = 1,
  totalPage = 1,
  pageNoOfLayoutPage = 1,
  totalPageOfLayoutPage = 1,
  onLayoutResolved = NOOP,
}) => {
  const resourceCode = getReportResourceCode(item)
  const onLayoutResolvedRef = useRef(onLayoutResolved)
  const [state, setState] = useState({
    loading: true,
    report: null,
    error: '',
  })

  useEffect(() => {
    onLayoutResolvedRef.current = onLayoutResolved
  }, [onLayoutResolved])

  const loadResource = useCallback(async () => {
    if (!resourceCode) {
      setState({ loading: false, report: null, error: 'Report not found' })
      return
    }

    setState((current) => ({ ...current, loading: true, error: '' }))

    try {
      const query = getResourceRequestParams(resourceCode, reportParams)
      const reportData = await fetchReportResource(query)

      if (!reportData?.reportTemplateId) {
        throw new Error('Report not found')
      }
      if (!isReportResourceOutput(reportData)) {
        throw new Error('Report not found')
      }

      onLayoutResolvedRef.current(getReportResourceSize(reportData.layoutData))
      setState({
        loading: false,
        report: resolveReportResourceData(reportData, reportParams),
        error: '',
      })
    } catch (error) {
      setState({
        loading: false,
        report: null,
        error: 'Report not found',
      })
    }
  }, [resourceCode, reportParams])

  useEffect(() => {
    loadResource()
  }, [loadResource])

  useEffect(() => {
    const reloadVisibleResource = () => {
      if (
        typeof document === 'undefined' ||
        document.visibilityState === 'visible'
      ) {
        loadResource()
      }
    }

    window.addEventListener('focus', reloadVisibleResource)
    document.addEventListener('visibilitychange', reloadVisibleResource)

    return () => {
      window.removeEventListener('focus', reloadVisibleResource)
      document.removeEventListener('visibilitychange', reloadVisibleResource)
    }
  }, [loadResource])

  if (state.loading) {
    return (
      <div style={{ ...statusStyle, borderColor: '#adb5bd', color: '#6c757d' }}>
        <Spinner animation="border" size="sm" />
        <small className="mt-1">Loading {resourceCode}</small>
      </div>
    )
  }

  if (state.error || !state.report) {
    return (
      <div style={statusStyle}>
        <strong>Report not found</strong>
        <small>{resourceCode || 'Missing report code'}</small>
      </div>
    )
  }

  const sourceLayout = state.report.layoutData || {}
  const sourceBoxes = state.report.printData || {}
  const dataSourceList = state.report.dataSourceData?.list || []
  const sourceBoxKeys = Object.keys(sourceBoxes).filter(
    (key) => (sourceBoxes[key]?.page || 1) === 1,
  )

  return (
    <div
      className={getFontClassName(sourceLayout.fontFamily)}
      style={{
        height: '100%',
        overflow: 'hidden',
        pointerEvents: 'none',
        position: 'relative',
        width: '100%',
      }}
    >
      {sourceBoxKeys.map((key) => {
        const sourceItem = sourceBoxes[key]

        return (
          <Box
            key={key}
            id={'resource-' + resourceCode + '-' + key}
            left={sourceItem.left}
            top={sourceItem.top}
            width={sourceItem.width}
            height={sourceItem.height}
            item={sourceItem}
            disabled
            layouts={sourceLayout}
            dataSourceList={dataSourceList}
            pageNo={pageNo}
            totalPage={totalPage}
            pageNoOfLayoutPage={pageNoOfLayoutPage}
            totalPageOfLayoutPage={totalPageOfLayoutPage}
            onClick={() => {}}
            onChange={() => {}}
          />
        )
      })}
    </div>
  )
}

export default ReportResourceContent
